// One validator for the creator form (app, site) and the server (backend), so
// a poll the phone accepts is a poll the server accepts.
import { ANSWERS_MAX, ANSWERS_MIN, ANSWER_MAX, QUESTION_MAX } from "./limits";
import type { PollDraft, QuestionDraft } from "./contracts";

/** Error codes are i18n keys under `create.errors.*`, never sentences. */
export type DraftError = (
  | { field: "question"; code: "required" | "too_long" }
  | { field: "answers"; code: "too_few" | "too_many" }
  | { field: "answer"; index: number; code: "too_long" | "duplicate" }
) & {
  /** In a question set, which question (0-based) the error is in; absent for the first. */
  set?: number;
};

export type DraftResult =
  | { ok: true; draft: PollDraft }
  | { ok: false; errors: DraftError[] };

/** Trim and collapse runs of whitespace (including newlines) to one space. */
export function cleanText(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}

/**
 * Normalizes and checks a draft. Blank answers are dropped silently (an empty
 * third field the creator never filled is not an error); indices in errors
 * refer to the creator's ORIGINAL answer list so the form can mark the field.
 */
export function validateDraft(input: PollDraft): DraftResult {
  const first = validateQuestion(input);
  if (!first.ok || !input.more?.length) return first;
  const more: QuestionDraft[] = [];
  for (const [i, q] of input.more.entries()) {
    const r = validateQuestion(q);
    if (!r.ok) return { ok: false, errors: r.errors.map((e) => ({ ...e, set: i + 1 })) };
    const { more: _ignored, ...one } = r.draft;
    more.push(one);
  }
  return { ok: true, draft: { ...first.draft, more } };
}

/** Every question of a draft, in order: the first, then the set's others. */
export function draftQuestions(draft: PollDraft): QuestionDraft[] {
  const { more, ...first } = draft;
  return [first, ...(more ?? [])];
}

function validateQuestion(input: QuestionDraft): DraftResult {
  const errors: DraftError[] = [];
  const question = cleanText(input.question);
  if (!question) errors.push({ field: "question", code: "required" });
  else if ([...question].length > QUESTION_MAX) errors.push({ field: "question", code: "too_long" });

  const kept: { text: string; index: number }[] = [];
  input.answers.forEach((raw, index) => {
    const text = cleanText(raw);
    if (text) kept.push({ text, index });
  });

  const seen = new Map<string, number>();
  for (const { text, index } of kept) {
    if ([...text].length > ANSWER_MAX) errors.push({ field: "answer", index, code: "too_long" });
    const key = text.toLocaleLowerCase();
    if (seen.has(key)) errors.push({ field: "answer", index, code: "duplicate" });
    else seen.set(key, index);
  }

  if (kept.length < ANSWERS_MIN) errors.push({ field: "answers", code: "too_few" });
  if (kept.length > ANSWERS_MAX) errors.push({ field: "answers", code: "too_many" });

  if (errors.length) return { ok: false, errors };
  // An edit's answer mapping follows the answers that were kept (blank ones are dropped).
  const mapping = input.answerFrom ? { answerFrom: kept.map((k) => input.answerFrom![k.index] ?? null) } : {};
  return { ok: true, draft: { question, answers: kept.map((k) => k.text), multi: input.multi, ...(input.from !== undefined ? { from: input.from } : {}), ...mapping } };
}

/**
 * Splits pasted text into answers (BACKLOG #13: pasting a list splits it).
 * Strips bullets and numbering: `- a`, `* a`, `<bullet> a`, `1. a`, `1) a`, `a) a`.
 * A letter only counts as numbering with `)`, so "A. Smith" stays whole.
 * Returns null when the text is a single line, so the caller pastes normally.
 */
export function splitPastedAnswers(text: string): string[] | null {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.replace(/^\s*(?:[-*\u2022\u25E6\u2023]|\d{1,2}[.)]|[a-z]\))\s+/, ""))
    .map(cleanText)
    .filter(Boolean);
  return lines.length > 1 ? lines : null;
}
